import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { normalizeRenderableImageUrl } from '../lib/api';

const coercePrice = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const ProductCard = ({ product = {} }) => {
    const { addItem } = useCart();

    const price = coercePrice(product.price);
    const compareAtPrice = product.compare_at_price == null ? null : coercePrice(product.compare_at_price);
    const images = Array.isArray(product.images) ? product.images.filter(Boolean).map(normalizeRenderableImageUrl) : [];
    const primaryImage = normalizeRenderableImageUrl(product.main_image) || images[0];
    const stockQuantity = Number.isFinite(Number(product.stock_quantity)) ? Number(product.stock_quantity) : 0;
    const gradeOptions = Array.isArray(product.grade_options) ? product.grade_options.filter(Boolean) : [];
    const defaultGrade = product.default_grade || (gradeOptions.length > 0 ? gradeOptions[0] : null);
    const isOutOfStock = !product.in_stock || (product.track_inventory && stockQuantity <= 0);

    const handleAddToCart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isOutOfStock) return;
        addItem({
            ...product,
            price,
            compare_at_price: compareAtPrice,
            images,
            stock_quantity: stockQuantity,
        }, 1, { selected_grade: defaultGrade });
    };

    return (
        <Link 
            to={`/shop/${product.id}`} 
            className="product-card group relative bg-white border border-black/10 overflow-hidden block hover:border-black/20 transition-all duration-500"
            data-testid={`product-card-${product.id}`}
        >
            <div className="aspect-square overflow-hidden bg-maple-secondary">
                {(primaryImage || images.length > 0) ? (
                    <img 
                        src={primaryImage || images[0]} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-black/20">
                        <span className="font-mono text-sm">No Image</span>
                    </div>
                )}
            </div>

            {product.featured && (
                <div className="absolute top-4 left-4 bg-maple-red px-3 py-1">
                    <span className="font-mono text-xs text-white uppercase tracking-wider">Featured</span>
                </div>
            )}

            <div className="p-5 sm:p-6">
                <span className="text-black/40 text-xs font-mono uppercase tracking-wider">
                    {product.category}
                </span>
                <h3 className="font-anton text-lg sm:text-xl mt-2 text-maple-dark group-hover:text-maple-red transition-colors leading-tight">
                    {product.name}
                </h3>
                {product.size_value && product.size_unit && (
                    <p className="text-black/40 text-xs font-mono mt-1" data-testid={`product-size-${product.id}`}>
                        {product.size_value} {product.size_unit === 'oz' ? 'oz' : product.size_unit === 'ml' ? 'ml' : product.size_unit === 'liter' ? 'L' : product.size_unit === 'quart' ? 'Quart' : product.size_unit === 'half_gallon' ? 'Half Gallon' : product.size_unit === 'gallon' ? 'Gallon' : product.size_unit}
                    </p>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                    <div className="flex items-baseline gap-2">
                        <span className="font-mono text-lg text-maple-dark">
                            ${price.toFixed(2)}
                        </span>
                        {compareAtPrice !== null && compareAtPrice > price && (
                            <span className="font-mono text-sm text-black/30 line-through">
                                ${compareAtPrice.toFixed(2)}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={handleAddToCart}
                        disabled={isOutOfStock}
                        className={`w-full sm:w-auto px-4 py-2.5 text-xs font-manrope uppercase tracking-wider transition-all duration-300 ${
                            isOutOfStock 
                                ? 'bg-black/5 text-black/30 cursor-not-allowed' 
                                : 'bg-black/5 hover:bg-maple-red hover:text-white text-maple-dark'
                        }`}
                        data-testid={`add-to-cart-${product.id}`}
                    >
                        {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                    </button>
                </div>
            </div>

            {isOutOfStock && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center pointer-events-none">
                    <span className="font-anton text-2xl text-black/30">Out of Stock</span>
                </div>
            )}
        </Link>
    );
};

export default ProductCard;
